import { Controller } from "@hotwired/stimulus"

// The calorie figure on a picker food row, for the amount actually selected.
//
// THE ROW USED TO SHOW "80/100g" — the library's reference figure — sitting
// beside a form set to "1 medium". Two numbers on one line, answering
// different questions, and the one you came for was the one missing. The
// designs had calories tracking the quantity; this is that.
//
// ARITHMETIC ONLY, NO ROUND TRIP. Every per-unit amount is already on the
// page (Food#entry_units renders them into the unit list), and the sum is
// kcal_per_100 × grams / 100. Asking the server would put a network delay
// on a number that changes on every press of +.
//
// The server stays the authority on what gets SAVED: this only previews the
// figure for a quantity that has not been submitted yet. Nothing here is
// written to the database, so a rounding difference between this and
// Food#macros_for costs nothing — and both round only at the end.
export default class extends Controller {
  static targets = ["quantity", "unit", "output", "kcal", "protein", "carbs", "fat",
                    "amountLabel", "amountGrams"]
  static values = {
    kcalPer100: Number,
    // HOW MANY DECIMALS THE SERVER PRINTED. A food's own macros carry one,
    // because they are read off a packet — see shared/_figures_row. This
    // controller repaints them on every stepper press, so hardcoding whole
    // grams here made 2.2 g become 2 and 0.4 g become 0 the instant
    // anything was touched. Cody, 11 Sep: "the calories are right but all
    // macros are showing 0 grams... but when i ate it and open up the
    // logged food, it shows the macros properly."
    precision: { type: Number, default: 0 },
    proteinPer100: Number,
    carbsPer100: Number,
    fatPer100: Number,
    // { "medium": 118, "g": 1 } — grams (or ml) per one of that unit.
    units: { type: Object, default: {} },
    baseUnit: String
  }

  connect() {
    this.render()
  }

  // Recalculates from whatever the inputs now hold. Bound to the stepper's
  // hidden field and the unit list, both of which change without a submit.
  render() {
    const quantity = parseFloat(this.quantityTarget?.value)
    const grams = Number.isFinite(quantity) && quantity > 0 ? this.grams(quantity) : null

    if (this.hasOutputTarget) {
      const kcal = this.kcal()
      this.outputTarget.textContent = kcal === null ? "" : `${kcal} kcal`
    }

    // ALL FOUR MACROS, because protein is as often the reason for choosing a
    // food as energy is. Rounded the way the server rounds them, so a pill
    // does not flicker between "12" and "12g" on the round trip.
    this.paint("kcal", grams, this.kcalPer100Value, true)
    this.paint("protein", grams, this.proteinPer100Value)
    this.paint("carbs", grams, this.carbsPer100Value)
    this.paint("fat", grams, this.fatPer100Value)

    // THE AMOUNT LINE FOLLOWS TOO, or it describes the portion you started
    // from while the figures describe the one you set. It leads the panel —
    // "2 medium · 236 g" — so a stale one is the most visible thing on it.
    //
    // The label is left to the server on a fraction: the quantity ladder
    // runs in halves and quarters, and "1½" is FractionalQuantities' answer
    // rather than something to reimplement here in JS.
    if (this.hasAmountLabelTarget && Number.isFinite(quantity)) {
      const unit = this.unitTarget?.value
      const shown = Number.isInteger(quantity) ? quantity : this.amountLabelTarget.textContent.trim().split(" ")[0]
      this.amountLabelTarget.textContent = unit ? `${shown} ${this.unitLabel(quantity, unit)}` : `${shown}`
    }

    if (this.hasAmountGramsTarget) {
      // THE WEIGHT BESIDE THE AMOUNT, always whole — "47.5 g" there is a
      // pack figure, not a macro, and the view renders it with the
      // helper's default precision of 0.
      this.amountGramsTarget.textContent =
        grams === null ? "" : (grams < 1 ? "<1" : String(Math.round(grams)))
    }
  }

  // MIRRORS FoodUnit#label_for, which is where this rule lives — see that
  // method and its spec. This is the only copy, and it exists because the
  // rule has to run on every press of a stepper: a round trip for a plural
  // is a request per keypress.
  //
  // NARROWER THAN THE SERVER'S ON PURPOSE. It handles the cases a unit
  // PICKER can produce — a name from this food's own list — and takes a
  // plain "s" for the rest. The irregulars ("berries", "bunches") are
  // spelled by the inflector on first render, and a stepper only ever moves
  // between quantities of a unit already on screen, so the singular it
  // would have to re-derive is one it never sees.
  unitLabel(quantity, unit) {
    // Kept in step with FoodUnit::SIZE_WORDS and ::INVARIANT.
    const size = ["small", "medium", "large", "whole", "half"]
    const measure = ["tbsp", "tsp", "g", "ml", "kg", "l", "oz"]

    if (size.includes(unit) || measure.includes(unit)) return unit
    if (quantity === 1) return unit
    if (unit.endsWith("s")) return unit

    const [head, ...rest] = unit.split(/([\s,].*)/)
    return `${head}s${rest.join("")}`
  }

  paint(name, grams, per100, kcal = false) {
    const capitalised = name[0].toUpperCase() + name.slice(1)
    if (!this[`has${capitalised}Target`]) return

    const target = this[`${name}Target`]
    const text = grams === null || !Number.isFinite(per100)
      ? "—"
      : (kcal ? String(Math.round((per100 * grams) / 100))
              : this.grams_label((per100 * grams) / 100))

    this.setFigure(target, text)
  }

  // THE NUMBER ONLY, leaving the label and the unit alone.
  //
  // A chip reads "P 1g" — three elements, of which this owns one. Setting
  // textContent would replace all three and leave a bare number, so the
  // chips would lose what they say the moment you touched the stepper.
  //
  // The figure is the chip's own text node; the label and unit are spans, so
  // walking the child nodes finds it without either being disturbed.
  setFigure(element, text) {
    const node = Array.from(element.childNodes)
      .find((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim())

    if (node) {
      node.textContent = text
    } else {
      element.textContent = text
    }
  }

  // MIRRORS ApplicationHelper#macro_grams EXACTLY, precision included —
  // "0" for nothing, "<1" below a gram, and above that the same number of
  // decimals the server printed. Any disagreement shows up as a figure
  // that changes the moment you touch the stepper and changes back on the
  // next full render, which is exactly how this was found.
  grams_label(value) {
    if (value === 0) return "0"
    if (value < 1) return "<1"

    return value.toFixed(this.precisionValue)
  }

  kcal() {
    const quantity = parseFloat(this.quantityTarget?.value)
    if (!Number.isFinite(quantity) || quantity <= 0) return null

    return Math.round((this.kcalPer100Value * this.grams(quantity)) / 100)
  }

  // Mirrors Food#grams_for: an unknown unit is worth one, which is what the
  // base unit is worth and the safest thing to assume for anything else.
  grams(quantity) {
    const unit = this.hasUnitTarget ? this.unitTarget.value : this.baseUnitValue
    const per = this.unitsValue[unit]

    return quantity * (per === undefined ? 1 : per)
  }
}
